import axios from 'axios';

import useLayoutStore from 'hooks/stores/useLayoutStore';

import API from 'services/API';

import { getLayout } from 'utils/layout';
import { sanitizeFileName } from 'utils/misc';

// Aantal sides dat bij upload direct een preview krijgt in de booklayout-view. Het grid toont
// tientallen pagina's tegelijk; de SidesList van layout=book maar ~10, dus die houdt de
// sessie-optie (options.onUpload.generatePreviewForSides). De worker neemt het maximum van
// beide, en laat "alles" (optie afwezig) ongemoeid.
export const BOOKLAYOUT_UPLOAD_PREVIEW_SIDES = 50;

export type UploadAssetFileParams = {
  sessionId: string;
  file: File;
  // Product waaraan de nieuwe sides toegevoegd worden; undefined = laat de worker kiezen.
  productId?: string;
  // 0-100, alleen voor de PUT naar de signed url (de verwerking daarna komt via de websocket).
  onProgress?: (percent: number) => void;
};

export const isSupportedUploadFile = (file: File): boolean => {
  const type = file.type.toLowerCase();
  return type.includes('pdf') || type.includes('image');
};

// Het volledige upload-pad voor één bestand: signed url ophalen, bestand naar storage zetten en
// de worker een bericht sturen om het te verwerken. Gedeeld door de toolbar-upload
// (AssetsAndSidesActions) en de eerste vrije positie in de booklayout-grid (PagePlaceholder).
export const uploadAssetFile = async ({
  sessionId,
  file,
  productId,
  onProgress,
}: UploadAssetFileParams): Promise<void> => {
  const timestampedFileName = sanitizeFileName(file.name);
  const signedUrl = await API.getSignedUrl(sessionId, file, timestampedFileName);
  if (!signedUrl) throw new Error('Failed to get signed url');

  const response = await axios.put(signedUrl, file, {
    headers: { 'Content-Type': file.type },
    onUploadProgress: (progressEvent) => {
      if (!onProgress || !progressEvent.total) return;
      onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
    },
  });
  if (response.status !== 200) throw new Error('Upload failed');

  const previewSidesCount =
    getLayout() === 'booklayout' ? BOOKLAYOUT_UPLOAD_PREVIEW_SIDES : undefined;
  await API.postNewUploadMessage(
    sessionId,
    file,
    timestampedFileName,
    productId,
    previewSidesCount,
  );
  // Laat de realtime verwerkingsbalk zien zodra de worker aan de slag gaat.
  useLayoutStore.getState().setShouldDisplayAssetsProcessing(true);
};
